import { LucideIcon, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

interface DashboardMetricCardProps {
  title: string
  value: string | number
  icon: LucideIcon
  iconColor: string
  iconBg: string
  badge?: {
    text: string
    variant: 'critical' | 'warning' | 'info' | 'success' | 'default'
  }
  trend?: {
    value: number
    isGoodWhenUp: boolean
  }
  onClick?: () => void
}

export default function DashboardMetricCard({
  title,
  value,
  icon: Icon,
  iconColor,
  iconBg,
  badge,
  trend,
  onClick
}: DashboardMetricCardProps) {
  const getTrendIcon = (value: number) => {
    if (value > 0) return <ArrowUpRight className="w-3 h-3" />
    if (value < 0) return <ArrowDownRight className="w-3 h-3" />
    return <Minus className="w-3 h-3" />
  }

  const getTrendColor = (value: number, isGoodWhenUp: boolean = true) => {
    if (value === 0) return 'text-gray-500'
    if (isGoodWhenUp) {
      return value > 0 ? 'text-success-green' : 'text-critical-red'
    } else {
      return value > 0 ? 'text-critical-red' : 'text-success-green'
    }
  }

  return (
    <Card 
      className={`card-predictive hover:shadow-md transition-all ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? title : undefined}
      onKeyDown={(e) => {
        if (!onClick) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-2 ${iconBg} rounded-lg`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
          {badge && (
            <Badge variant={badge.variant}>{badge.text}</Badge>
          )}
        </div>
        <div className="space-y-1">
          <h3 className="text-3xl font-bold text-gray-900 tabular-nums">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </h3>
          <p className="text-sm text-gray-600 uppercase tracking-wide font-medium">
            {title}
          </p>
          {trend && (
            <div className={`flex items-center gap-1 text-xs ${getTrendColor(trend.value, trend.isGoodWhenUp)}`}>
              {getTrendIcon(trend.value)}
              <span>
                {Math.abs(trend.value)}% vs last period
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}